/**
 * Handoff between the booking wizard and the `/booking/return` page.
 *
 * Paying by card takes the guest off-site to Viva Smart Checkout, so the wizard
 * state is gone by the time they come back. We keep just enough here to render
 * a proper confirmation and fire the purchase event — never anything the server
 * would have to trust, since the return page re-confirms everything with the
 * `confirmCardPayment` callable.
 *
 * localStorage rather than sessionStorage: some payment methods hand the guest
 * back in a different tab.
 */

export interface CardCheckoutHandoff {
  orderCode: string;
  paymentId: string;
  bookingCode: string;
  amount: number;
  email: string;
  experienceAlias: string | null;
  experienceTitle: string | null;
  optionTitle: string | null;
  activityDateTime: string;
  guests: number;
  /** Epoch ms, used to drop stale handoffs from an abandoned attempt. */
  startedAt: number;
}

const KEY = 'fm_card_checkout';
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

export function rememberCardCheckout(handoff: CardCheckoutHandoff): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(handoff));
  } catch {
    /* Private mode / quota — the return page copes without it. */
  }
}

/**
 * Read the stored handoff back. When `orderCode` is given, only a handoff for
 * that same order is returned, so a stale one can never mislabel a payment.
 */
export function readCardCheckout(
  orderCode?: string | null,
): CardCheckoutHandoff | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CardCheckoutHandoff;
    if (!parsed?.orderCode) return null;
    if (Date.now() - (parsed.startedAt ?? 0) > MAX_AGE_MS) return null;
    if (orderCode && parsed.orderCode !== orderCode) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function forgetCardCheckout(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* Nothing to clean up. */
  }
}

/* ------------------------------------------------------------------ */
/* Purchase de-duplication                                             */
/* ------------------------------------------------------------------ */

const REPORTED_KEY = 'fm_card_purchase_reported';
const REPORTED_MAX = 20;

/**
 * Order codes whose `purchase` event has already gone to GA4.
 *
 * The return page is a normal URL the guest can reload or revisit from history,
 * and each mount would otherwise re-fire `purchase` for the same booking. GA4
 * does not reliably de-duplicate on `transaction_id`, so revenue would inflate.
 * A short ring of recent codes is enough — old ones can never come back.
 */
function readReported(): string[] {
  try {
    const raw = localStorage.getItem(REPORTED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function wasPurchaseReported(orderCode: string): boolean {
  return readReported().includes(orderCode);
}

export function markPurchaseReported(orderCode: string): void {
  try {
    const next = [orderCode, ...readReported().filter((c) => c !== orderCode)];
    localStorage.setItem(REPORTED_KEY, JSON.stringify(next.slice(0, REPORTED_MAX)));
  } catch {
    /* Without storage we may double-count a reload; nothing better to do. */
  }
}
